import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import { config } from 'Constants';

import { useFormik } from 'formik';

import MDButton from 'components/MDButton';
import { postPervaz, updatePervaz } from 'services/api/form';
import { getTags } from 'services/api/general/tag';
import Grid from '@mui/material/Grid';
import FormSelect from '../FormComponents/FormSelect';
import EmptySpace from '../FormComponents/EmptySpace';
import FormTextFieldTitle from '../FormComponents/FormTextFieldTitle';
import FormSwitch from '../FormComponents/FormSwitch';
import OutlinedFormTextField from '../FormComponents/OutlinedFormTextField';
import { validationSchemaPervaz } from '../FormComponents/YupControlSchema';
import AaroLoadingScreen from 'components/Loading';

const switchStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
};

const fetchTags = async (id) => {
    const response = await getTags(id).then((rp) => (rp.data?.result?.tags ? rp.data?.result?.tags : []));
    return response;
};
const TAG_GROUP_ID_DEMAND = config.fixedID.DEMAND_STATUS_TAG_GROUP_ID;
export const Pervaz = (props) => {
    const queryClient = useQueryClient();
    const {
        data: tags,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['fetchTagGroups', TAG_GROUP_ID_DEMAND],
        queryFn: () => fetchTags(TAG_GROUP_ID_DEMAND),
        staleTime: 600000,
    });
    const user = JSON.parse(localStorage.getItem('user'));
    const [disableOnSubmit, setDisableOnSubmit] = React.useState(false);
    const { demandID, initial, id, setOpen, projectID } = props;

    const formik = useFormik({
        initialValues: {
            baslik: initial?.baslik || 'Standart Pervaz',
            miktar: initial?.miktar || null,
            kalinlik: initial?.kalinlik || null,
            en: initial?.en || null,
            boy: initial?.boy || null,
            cins: initial?.cins || '',
            cinsAciklama: initial?.cinsAciklama || '',
            koseliBombeli: initial?.koseliBombeli || 'Köşeli',
            yapi: initial?.yapi || 'L Pervaz',
            gecmeKalinlik: initial?.gecmeKalinlik || 0.4,
            gecmeIctenUzunluk: initial?.gecmeIctenUzunluk || 2.5,
            hamZimpara: initial?.hamZimpara || false,
            boyKesmeKertme: initial?.boyKesmeKertme || false,
            ozelProfilTipi: initial?.ozelProfilTipi || 'Standart',
            ozelProfilAciklama: initial?.ozelProfilAciklama || '',
            ekNot: initial?.ekNot || '',
            indirimOrani: initial?.indirimOrani || 0,
            yoneticiNotu: initial?.yoneticiNotu || '',
            verilenFiyat: initial?.verilenFiyat || null,
            durumu: initial?.durumu || '',
        },
        validationSchema: validationSchemaPervaz,
        onSubmit: (values) => {
            setDisableOnSubmit(true);
            if (id) {
                updatePervaz(id, values)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demandID] });
                        setOpen(false);
                    })
                    .catch((err) => {
                        console.log(err);
                        setDisableOnSubmit(false);
                    });
            } else {
                postPervaz(demandID, values)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demandID] });
                        setOpen(false);
                    })

                    .catch((err) => {
                        console.log(err);
                        setDisableOnSubmit(false);
                    });
            }
        },
    });
    if (isLoading) return <AaroLoadingScreen />;

    if (error) return 'Bir Hata Oluştu: ' + error.message;
    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                    }}
                >
                    {/* Başlık */}
                    <FormTextFieldTitle
                        variant="filled"
                        fullWidth={true}
                        label="Başlık"
                        placeHolder="Örn: Pervaz Başlık"
                        formik={formik}
                        itemID="baslik"
                        disabled={true}
                        selector="pervaz"
                    />
                    <EmptySpace height="30px" />
                    {/* Miktar - Kalınlık - En - Boy */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Miktar */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="miktar"
                                label="Miktar (Adet)"
                                placeHolder="Örn: 1000"
                                type="number"
                                formik={formik}
                                step="1"
                                adornment="AD"
                            />
                        </Grid>
                        {/* Kalınlık */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="kalinlik"
                                label="Kalınlık"
                                placeHolder="Örn: 0.9"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* En */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="en"
                                label="En"
                                placeHolder="Örn: 8"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Boy */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="boy"
                                label="Boy"
                                placeHolder="Örn: 219"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                    </Grid>
                    {/* Geçme Kalınlık - Geçme İçten Uzunluk */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Geçme Kalınlık */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="gecmeKalinlik"
                                label="Geçme Kalınlık (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Geçme İçten Uzunluk*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="gecmeIctenUzunluk"
                                label="Geçme İçten Uzunluk (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Ham Zımpara - Boy Kesme Kertme*/}
                    <Grid container spacing={3} sx={switchStyles}>
                        {/* Ham Zımpara */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 6,
                            }}
                        >
                            <FormSwitch formik={formik} name="hamZimpara" color="secondary" label="Ham Zımpara" />
                        </Grid>
                        {/* Boy Kesme Kertme*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 6,
                            }}
                        >
                            <FormSwitch
                                formik={formik}
                                name="boyKesmeKertme"
                                color="secondary"
                                label="Boy Kesme ve Kertme"
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Yapı - Köşeli/Bombeli */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Yapı */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <FormSelect
                                inputName="Yapı"
                                itemID="yapi"
                                items={[
                                    {
                                        name: 'L Pervaz',
                                        value: 'L Pervaz',
                                    },
                                    {
                                        name: 'Düz Pervaz',
                                        value: 'Düz Pervaz',
                                    },
                                ]}
                                formik={formik}
                            />
                        </Grid>
                        {/* Köşeli/Bombeli*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <FormSelect
                                inputName="Köşeli/Bombeli"
                                itemID="koseliBombeli"
                                items={[
                                    { value: 'Köşeli', name: 'Köşeli' },
                                    { value: 'Bombeli', name: 'Bombeli' },
                                ]}
                                formik={formik}
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Cins - Cins Açıklama*/}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Cins */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <FormSelect
                                inputName="Cins"
                                itemID="cins"
                                items={[
                                    {
                                        name: 'Lakelik',
                                        value: 'Lakelik',
                                    },
                                    {
                                        name: 'Desenli',
                                        value: 'Desenli',
                                    },
                                    {
                                        name: 'Kaplamalı Alpi',
                                        value: 'Kaplamalı Alpi',
                                    },
                                    {
                                        name: 'Kaplamalı Doğal',
                                        value: 'Kaplamalı Doğal',
                                    },
                                    {
                                        name: 'Kaplama Müşteriden',
                                        value: 'Kaplama Müşteriden',
                                    },
                                    {
                                        name: 'Özel Kaplama Doğal',
                                        value: 'Özel Kaplama Doğal',
                                    },
                                    {
                                        name: 'Özel Kaplama Alpi',
                                        value: 'Özel Kaplama Alpi',
                                    },
                                ]}
                                formik={formik}
                            />
                        </Grid>
                        {/* Cins Açıklama*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="cinsAciklama"
                                label="Cins Açıklama"
                                placeHolder="Örn: Melamin Beyaz"
                                type="text"
                                formik={formik}
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Özel Profil Tipi - Özel Profil Açıklama*/}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Özel Profil Tipi */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <FormSelect
                                inputName="Özel Profil Tipi"
                                itemID="ozelProfilTipi"
                                items={[
                                    {
                                        name: 'Standart',
                                        value: 'Standart',
                                    },
                                    {
                                        name: 'Farklı Radüsler',
                                        value: 'Farklı Radüsler',
                                    },
                                    {
                                        name: 'Özel Profil',
                                        value: 'Özel Profil',
                                    },
                                    {
                                        name: 'Özel Profil + Bıçak',
                                        value: 'Özel Profil + Bıçak',
                                    },
                                ]}
                                formik={formik}
                            />
                        </Grid>
                        {/* Özel Profil Açıklama*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 6,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="ozelProfilAciklama"
                                label="Özel Profil Açıklama"
                                placeHolder="Örn: ..."
                                formik={formik}
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Durumu - Ek Not*/}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Ek Not*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="ekNot"
                                label="Ek Not"
                                placeHolder="Örn: ..."
                                type="text"
                                formik={formik}
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Yönetici Notu - İndirim Oranı - Verilen Fiyat*/}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Yönetici Notu */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="yoneticiNotu"
                                label="Yönetici Notu"
                                placeHolder="Örn: ..."
                                type="text"
                                formik={formik}
                            />
                        </Grid>
                        {/* Verilen Fiyat*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="verilenFiyat"
                                label="Verilen Fiyat"
                                placeHolder="Örn: 10"
                                type="number"
                                formik={formik}
                                step="0.01"
                                adornment="TL"
                            />
                        </Grid>
                        {/* Durumu */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            <FormSelect
                                inputName="Durumu"
                                itemID="durumu"
                                items={tags ? tags.map((tag) => ({ value: tag._id, name: tag.name })) : []}
                                formik={formik}
                            />
                        </Grid>
                    </Grid>
                    <EmptySpace height="30px" />
                    {/* Kaydet */}
                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <MDButton
                                variant="gradient"
                                color="dark"
                                type="submit"
                                fullWidth
                                disabled={disableOnSubmit}
                            >
                                {id ? 'GÜNCELLE' : 'KAYDET'}
                            </MDButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};
